import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../models/api/load";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { TnADayDataRef } from "../../../../models/tna/tna-data/tna-day-data-ref";
import { TnACalendarDaysDTO } from "../../../../models/tna-calendar-days";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import {
  getDayAndMonth,
  getDaysInMonth,
  getNormaleDateOrMonth,
  intlFormatDateShort,
  intlFormatDateWithHour,
} from "../../../../utils/time-utils";
import { monthsValues } from "../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import { getIntervalsDuration } from "../../../SystemConfigurationTables/TnAConfig/TnASchedulesTable/TnAScheduleConfig/utils";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnACalendarsData: (pageSize?: number) => Promise<LoadResponse<TnACalendarDaysDTO> | undefined>;
  allItems: () => TnACalendarDaysDTO[];
}

const ExportTnACalendarToExcel = ({ disabled, loadEmployeeGroup, loadTnACalendarsData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_calendar";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Time and attendance calendars"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [`Period: ${year} ${t(monthsValues.find((currMonth) => currMonth.value === month)?.key ?? "")}`],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);

    const dates: string[] = [];

    for (let i = 0; i < daysInMonth; i++) {
      dates.push(getDayAndMonth(new Date(year, month, i + 1)));
    }

    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      ...dates,
    ];
  }, [year, month]);

  const dataToExportAsExcel = useCallback(
    (allItems: TnACalendarDaysDTO[]) => {
      const daysInMonth = getDaysInMonth(year, month);

      return allItems.map((item) => {
        const dates: { [key: string]: { value: string; style?: any } } = {};

        for (let i = 0; i < daysInMonth; i++) {
          const currDay = item?.daysData?.find(
            (day: TnADayDataRef) => day?.tnADate?.day === i + 1 && day.tnADate.month - 1 === month
          );

          const isWeekend = [0, 6].indexOf(new Date(year, month, i + 1).getDay()) !== -1;
          const weekday = currDay?.tnADate ? CustomDate.ToDate(currDay?.tnADate)?.getDay() : null;
          const notWorkingDay = currDay?.isPublicHoliday || isWeekend;

          const absences = currDay?.absences
            .map(
              (absence) =>
                `${absence.absenceType?.code}\n${
                  absence.isCancelled
                    ? `(Cancelled)`
                    : absence?.isApproved
                    ? `Accepted by ${absence?.modifiedByUser?.name}`
                    : absence?.isRejected
                    ? `Rejected by ${absence?.modifiedByUser?.name}`
                    : ""
                } ${
                  !absence.isCancelled &&
                  absence.cancelRequested &&
                  absence.cancelRequestedAt &&
                  absence.cancellationRequestApproved === null
                    ? ` \n (Cancel requested)`
                    : ""
                }`
            )
            .join(", ")
            .trim();

          const cellContentValue = `${
            currDay?.schedule
              ? currDay.schedule?.code.trim()
                ? ` ${currDay.schedule?.code?.trim()} (${(
                    getIntervalsDuration(
                      currDay.schedule?.intervals.filter((interval) => interval.weekDay === weekday)
                    ) /
                    (1000 * 60 * 60)
                  ).toFixed(2)} h)`
                : ""
              : ""
          } ${absences ? `${currDay?.schedule?.code.trim() ? `\n` : ""}${absences}` : ""} `;

          dates[getDayAndMonth(new Date(year, month, i + 1))] = {
            value: cellContentValue,
            style: {
              backgroundColor: isWeekend
                ? excelStyles.weekendCell.backgroundColor
                : currDay?.isPublicHoliday
                ? excelStyles.publicHolidayCellStyle.backgroundColor
                : excelStyles.normalCell.backgroundColor,
            },
          };
        }

        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          ...dates,
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, year, month]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } TnA Calendars`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnACalendarsData();

        if (firstResponse?.error) return;

        const allTnACalendarsData = await loadTnACalendarsData(firstResponse?.totalItems);

        if (!!allTnACalendarsData?.error || !allTnACalendarsData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnACalendarsData?.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } TnA Calendars`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnACalendarsData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportTnACalendarToExcel;
